// src/pages/ZakatPage.js

import React, { useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Input, message, Spin } from "antd";
import {
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  InformationCircleIcon,
  LockClosedIcon,
  UserIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";

import {
  resolveRecipient as resolveRecipientApi,
  normalizePhone,
} from "../services/baitCashApi";
import "../components/Walletview.css";

const USERS_API = "https://backend.bait-coin.com/api/users";
const ELIGIBILITY_API = "https://backend.bait-coin.com/api/check-eligibility";

const ZakatPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { wallet, baitcashAccount } = location.state || {};

  // Step state: 'eligibility' | 'send' | 'success'
  const [step, setStep] = useState("eligibility");
  const [eligible, setEligible] = useState(false);
  const [eligibilityResult, setEligibilityResult] = useState(null);
  const [suggestedZakatBAIT, setSuggestedZakatBAIT] = useState(0);
  const [eligibilityError, setEligibilityError] = useState("");

  // Eligibility form data
  const [formData, setFormData] = useState({
    name: "",
    cnic: "",
    age: "",
    income: "",
    liabilities: "",
    assets: "",
    employment: "",
    support: "",
    residentialStatus: "",
  });

  // Send Zakat state
  const [zakatPhone, setZakatPhone] = useState("");
  const [zakatAmountPKR, setZakatAmountPKR] = useState("");
  const [recipient, setRecipient] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sendError, setSendError] = useState("");
  const [successData, setSuccessData] = useState(null);

  const phoneInputRef = useRef(null);

  // Redirect if missing data
  if (!wallet || !baitcashAccount) {
    navigate("/dashboard");
    return null;
  }

  const goBack = () => navigate("/dashboard");

  // Format PKR
  const formatPkr = (value) => {
    if (!Number.isFinite(Number(value))) return "₨ 0.00";
    return `₨ ${Number(value).toLocaleString("en-PK", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // ---------- Eligibility Handlers ----------
  const handleEligibilityChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEligibilitySubmit = async (e) => {
    e.preventDefault();
    setEligibilityError("");
    setEligible(false);
    setEligibilityResult(null);

    if (
      !formData.name ||
      !formData.cnic ||
      !formData.age ||
      !formData.income
    ) {
      setEligibilityError("Please fill in all required fields.");
      return;
    }

    try {
      const response = await axios.post(ELIGIBILITY_API, formData);
      const data = response.data;

      if (data.eligible) {
        setEligible(true);
        setEligibilityResult(
          data.message || "You are eligible to receive Zakat."
        );
        const income = parseFloat(formData.income);
        let baitZakatPKR = 0;
        if (income <= 10000) {
          baitZakatPKR = 30000;
        } else if (income <= 20000) {
          baitZakatPKR = 20000;
        } else if (income <= 40000) {
          baitZakatPKR = 10000;
        }
        const pricePerGram = 29838;
        const gramsPerBait = 4.25;
        const baitCoins = baitZakatPKR / (pricePerGram * gramsPerBait);
        setSuggestedZakatBAIT(baitCoins);
        const suggestedPKR = baitZakatPKR;
        setZakatAmountPKR(suggestedPKR.toFixed(2));
        setStep("send");
        message.success("Eligibility confirmed! You can now send Zakat.");
      } else {
        setEligible(false);
        setEligibilityResult(
          data.message || "You are not eligible for Zakat."
        );
        setStep("eligibility");
      }
    } catch (err) {
      console.error("Eligibility check failed:", err);
      setEligibilityError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to check eligibility. Please try again."
      );
    }
  };

  // ---------- Send Zakat Handlers ----------
  const resolveRecipient = async (phoneOverride = null) => {
    if (resolving) return;
    const phone = phoneOverride || normalizePhone(zakatPhone);
    if (!phone) {
      setSendError("Please enter a valid phone number.");
      return;
    }

    if (!baitcashAccount?.phoneNumber) {
      setSendError("Your BAIT Cash account could not be identified.");
      return;
    }

    const senderNormalized = normalizePhone(baitcashAccount.phoneNumber);
    if (phone === senderNormalized) {
      setSendError("You cannot send Zakat to yourself.");
      return;
    }

    setResolving(true);
    setSendError("");
    setRecipient(null);

    try {
      const data = await resolveRecipientApi(phone);
      if (data?.recipient) {
        setRecipient(data.recipient);
        setSendError("");
        message.success(`Recipient found: ${data.recipient.fullName}`);
      } else {
        throw new Error("Recipient BAIT Cash account was not found.");
      }
    } catch (err) {
      setRecipient(null);
      setSendError(
        err?.message || "Unable to find this BAIT Cash account."
      );
    } finally {
      setResolving(false);
    }
  };

  const handlePhoneBlur = async () => {
    if (!zakatPhone.trim() || recipient) return;
    await resolveRecipient();
  };

  const handlePhoneKeyDown = async (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      phoneInputRef.current?.blur();
      await resolveRecipient();
    }
  };

  const validateAmount = () => {
    if (!zakatAmountPKR || zakatAmountPKR.trim() === "")
      return "Please enter a zakat amount.";
    if (!/^\d+(\.\d{1,2})?$/.test(zakatAmountPKR.trim()))
      return "Maximum 2 decimal places are allowed.";
    const value = Number(zakatAmountPKR);
    if (!Number.isFinite(value)) return "Please enter a valid amount.";
    if (value <= 0) return "Amount must be greater than zero.";
    if (value > Number(baitcashAccount?.balance))
      return "Insufficient BAIT Cash balance.";
    return "";
  };

  const handleSendZakat = async () => {
    setSendError("");
    const amountError = validateAmount();
    if (amountError) {
      setSendError(amountError);
      return;
    }
    if (!recipient) {
      setSendError("Please enter a valid recipient phone number.");
      return;
    }

    setSubmitting(true);
    try {
      const senderPhone = baitcashAccount.phoneNumber;
      const recipientPhone = recipient.phoneNumber;
      if (senderPhone === recipientPhone) {
        throw new Error("You cannot send Zakat to yourself.");
      }

      const idempotencyKey = `ZAKAT-${Date.now()}-${Math.random()
        .toString(36)
        .substring(2, 8)
        .toUpperCase()}`;

      const response = await fetch(`${USERS_API}/transfer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          senderPhone,
          recipientPhone,
          amountPKR: zakatAmountPKR,
          idempotencyKey,
          note: "Zakat payment",
        }),
      });

      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(
          data?.error || data?.message || "Zakat transfer failed."
        );
      }

      setSuccessData(data);
      setStep("success");
      message.success("Zakat sent successfully!");
    } catch (err) {
      setSendError(err?.message || "Zakat transfer failed.");
      message.error("Zakat transfer failed.");
    } finally {
      setSubmitting(false);
    }
  };

  // Reset and go back
  const resetAndGoBack = () => {
    setStep("eligibility");
    setEligible(false);
    setEligibilityResult(null);
    setFormData({
      name: "",
      cnic: "",
      age: "",
      income: "",
      liabilities: "",
      assets: "",
      employment: "",
      support: "",
      residentialStatus: "",
    });
    setZakatPhone("");
    setZakatAmountPKR("");
    setRecipient(null);
    setSendError("");
    setSuccessData(null);
    goBack();
  };

  // ==========================================================
  // STYLES
  // ==========================================================
  const styles = {
    page: {
      width: "100%",
      minHeight: "80vh",
      background: "#faf8f5",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      overflowX: "hidden",
    },

    container: {
      width: "calc(100% - 40px)",
      maxWidth: 540,
      margin: "0 auto 32px",
      padding: "0 0 16px",
      boxSizing: "border-box",
    },

    pageTitle: {
      width: "100%",
      marginBottom: 24,
      boxSizing: "border-box",
    },
    titleWrapper: {
      position: "relative",
      width: "100%",
      minHeight: 52,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxSizing: "border-box",
    },
    backButton: {
      position: "absolute",
      left: 0,
      top: "50%",
      transform: "translateY(-50%)",
      width: 36,
      height: 36,
      padding: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "rgba(154,106,24,0.06)",
      border: "1px solid rgba(154,106,24,0.12)",
      borderRadius: "10px",
      color: "#9a6a18",
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxSizing: "border-box",
      flexShrink: 0,
    },
    titleContent: {
      width: "100%",
      textAlign: "center",
      padding: "0 48px",
      boxSizing: "border-box",
    },
    title: {
      margin: "0 0 5px",

      fontFamily:
        "'Inter', 'Poppins', 'Segoe UI', sans-serif",

      fontSize: 26,

      lineHeight: 1.1,

      fontWeight: 600,

      letterSpacing: "1.4px",

      color: "#8a5a1f",

      WebkitTextStroke: "0.4px #6b3d0a",

      textShadow:
        "0 1px 0 rgba(255, 240, 200, 0.6), 0 2px 6px rgba(154, 106, 24, 0.22)",
    },
    subtitle: {
      margin: "4px 0 0",

      fontFamily:
        "'Poppins', 'Segoe UI', sans-serif",

      fontSize: 11,

      fontWeight: 500,

      letterSpacing: "0.6px",

      lineHeight: 1.3,

      color: "#a07a3f",

      opacity: 0.95,
    },

    formContainer: { width: "100%", boxSizing: "border-box" },
    successContainer: {
      width: "100%",
      boxSizing: "border-box",
      textAlign: "center",
    },

    errorBox: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      background: "#fef3e8",
      color: "#9a5b12",
      border: "1px solid #f5d9b8",
      borderRadius: "10px",
      padding: "8px 12px",
      marginBottom: "16px",
      fontSize: "12px",
      fontWeight: 500,
    },

    eligibilityCard: {
      background: "#ffffff",
      borderRadius: "16px",
      padding: "24px",
      border: "1px solid #ece4d6",
      boxShadow: "0 4px 16px rgba(0,0,0,0.03)",
      marginBottom: "20px",
    },
    formRow: {
      display: "flex",
      gap: "12px",
      marginBottom: "16px",
      flexWrap: "wrap",
    },
    formGroup: {
      flex: "1",
      minWidth: "150px",
    },
    formLabel: {
      display: "block",
      fontSize: "13px",
      fontWeight: 600,
      color: "#5a4a32",
      marginBottom: "6px",
    },
    formInput: {
      width: "100%",
      height: "48px",
      padding: "0 14px",
      borderRadius: "10px",
      border: "1px solid #e0d6c8",
      fontSize: "15px",
      fontWeight: 500,
      color: "#2a1f12",
      background: "#ffffff",
      transition: "border-color 0.2s, box-shadow 0.2s",
      boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
      outline: "none",
    },
    formSelect: {
      width: "100%",
      height: "48px",
      padding: "0 14px",
      borderRadius: "10px",
      border: "1px solid #e0d6c8",
      fontSize: "15px",
      fontWeight: 500,
      color: "#2a1f12",
      background: "#ffffff",
      outline: "none",
      appearance: "auto",
    },
    eligibilityResult: {
      marginTop: "16px",
      padding: "12px 16px",
      borderRadius: "10px",
      border: "1px solid #d1fae5",
      background: "#ecfdf5",
      color: "#065f46",
      fontSize: "13px",
      lineHeight: 1.5,
    },
    eligibilityError: {
      marginTop: "16px",
      padding: "12px 16px",
      borderRadius: "10px",
      border: "1px solid #fecaca",
      background: "#fef2f2",
      color: "#991b1b",
      fontSize: "13px",
      lineHeight: 1.5,
    },

    balanceCard: {
      background: "#eee3d4",
      borderRadius: "16px",
      padding: "20px 24px",
      marginBottom: "24px",
      border: "1px solid #f0ba5d",
      boxShadow: "0 4px 16px rgba(0,0,0,0.03)",
    },
    balanceLabel: {
      fontSize: "12px",
      fontWeight: 600,
      color: "#92703d",
      letterSpacing: "0.3px",
      textTransform: "uppercase",
      marginBottom: "4px",
    },
    balanceAmount: {
      fontSize: "32px",
      fontWeight: 800,
      color: "#2a1f12",
      margin: "2px 0 6px",
      letterSpacing: "-0.5px",
    },
    balanceUser: {
      fontSize: "13px",
      color: "#8a7a62",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    },

    inputRow: {
      display: "flex",
      gap: "8px",
      alignItems: "center",
    },
    phoneInput: {
      flex: 1,
      height: "48px",
      padding: "0 14px",
      borderRadius: "10px",
      border: "1px solid #e0d6c8",
      fontSize: "15px",
      fontWeight: 500,
      color: "#2a1f12",
      background: "#ffffff",
      transition: "border-color 0.2s, box-shadow 0.2s",
      boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
      outline: "none",
    },
    resolveButton: {
      height: "48px",
      padding: "0 16px",
      borderRadius: "10px",
      border: "none",
      background: "linear-gradient(135deg, #9a6a18, #c18a32)",
      color: "#ffffff",
      fontSize: "13px",
      fontWeight: 600,
      cursor: "pointer",
      whiteSpace: "nowrap",
      transition: "all 0.2s ease",
      boxShadow: "0 2px 8px rgba(154,106,24,0.12)",
    },
    formHelper: {
      marginTop: "4px",
      fontSize: "11px",
      color: "#9b8b73",
      lineHeight: 1.4,
    },

    amountInput: {
      width: "100%",
      height: "56px",
      padding: "0 16px",
      borderRadius: "10px",
      border: "1px solid #e0d6c8",
      fontSize: "26px",
      fontWeight: 700,
      color: "#2a1f12",
      background: "#ffffff",
      transition: "border-color 0.2s, box-shadow 0.2s",
      boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
      outline: "none",
    },

    recipientCard: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "12px 14px",
      background: "#f8f4ec",
      borderRadius: "12px",
      border: "1px solid #ece4d6",
      marginBottom: "18px",
    },
    recipientAvatar: {
      width: "40px",
      height: "40px",
      borderRadius: "50%",
      background: "linear-gradient(135deg, #f4ead9, #e8dcc8)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#9a6a18",
    },
    recipientInfo: { flex: 1, minWidth: 0 },
    recipientName: {
      fontSize: "14px",
      fontWeight: 700,
      color: "#2a1f12",
      margin: 0,
    },
    recipientPhone: { fontSize: "12px", color: "#8a7a62", margin: 0 },
    recipientCheck: { color: "#4cae5b", flexShrink: 0 },

    submitButton: {
      width: "100%",
      height: "52px",
      borderRadius: "12px",
      border: "none",
      background: "linear-gradient(135deg, #9a6a18, #c18a32)",
      color: "#ffffff",
      fontSize: "15px",
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      cursor: "pointer",
      transition: "all 0.2s ease",
      marginTop: "8px",
      boxShadow: "0 4px 16px rgba(154,106,24,0.18)",
    },

    securityNote: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      marginTop: "24px",
      padding: "12px 0",
      borderTop: "1px solid #ece4d6",
      fontSize: "12px",
      color: "#9b8b73",
    },
    securityIcon: { color: "#9a6a18" },

    successIcon: { color: "#4cae5b" },
    successTitle: {
      margin: "14px 0 2px",
      fontSize: "22px",
      fontWeight: 700,
      color: "#2a1f12",
    },
    successSubtitle: { fontSize: "13px", color: "#8a7a62" },
    successCard: {
      background: "#f8f4ec",
      borderRadius: "12px",
      padding: "14px 16px",
      margin: "10px 0",
      border: "1px solid #ece4d6",
      textAlign: "left",
    },
    successRow: {
      display: "flex",
      justifyContent: "space-between",
      padding: "6px 0",
      fontSize: "13px",
      color: "#5a4a32",
      borderBottom: "1px solid #ece4d6",
    },
  };

  // ==========================================================
  // RENDER
  // ==========================================================
  return (
    <div style={styles.page}>
      {/* Main content */}
      <div style={styles.container}>
        <div style={styles.pageTitle}>
          <div style={styles.titleWrapper}>
            {/* BACK BUTTON — uses shared .market-back-btn styles */}
        <button
          type="button"
          className="market-back-btn"
          onClick={goBack}
          title="Back to Dashboard"
          aria-label="Back to Dashboard"
        >
          <ArrowLeftIcon width={18} height={18} />
        </button>
            <div style={styles.titleContent}>
              <h1 style={styles.title}>
                {step === "eligibility"
                  ? "Zakat Eligibility"
                  : step === "send"
                  ? "Send Zakat"
                  : "Zakat Sent"}
              </h1>
              <p style={styles.subtitle}>
                {step === "eligibility" &&
                  "Check if you are eligible to receive Zakat"}
                {step === "send" && "Send Zakat via BAIT Cash"}
                {step === "success" &&
                  "Your Zakat has been sent successfully"}
              </p>
            </div>
          </div>
        </div>

        {step === "success" ? (
          // ---------- SUCCESS ----------
          <div style={styles.successContainer}>
            <div style={styles.successIcon}>
              <CheckCircleIcon width={60} height={60} />
            </div>
            <h2 style={styles.successTitle}>Zakat Sent!</h2>
            <p style={styles.successSubtitle}>
              Your Zakat has been sent successfully.
            </p>
            <div style={styles.successCard}>
              <div style={styles.successRow}>
                <span>Recipient</span>
                <strong>{recipient?.fullName}</strong>
              </div>
              <div style={styles.successRow}>
                <span>Phone</span>
                <strong>{recipient?.phoneNumber}</strong>
              </div>
              <div style={styles.successRow}>
                <span>Amount</span>
                <strong>{formatPkr(zakatAmountPKR)}</strong>
              </div>
              {successData?.transfer?.reference && (
                <div style={styles.successRow}>
                  <span>Reference</span>
                  <strong
                    style={{
                      fontSize: "11px",
                      wordBreak: "break-all",
                    }}
                  >
                    {successData.transfer.reference}
                  </strong>
                </div>
              )}
            </div>
            <button
              onClick={resetAndGoBack}
              style={styles.submitButton}
            >
              Done
            </button>
          </div>
        ) : step === "send" ? (
          // ---------- SEND ZAKAT ----------
          <div style={styles.formContainer}>
            {sendError && (
              <div style={styles.errorBox}>
                <InformationCircleIcon width={18} height={18} />
                <span>{sendError}</span>
              </div>
            )}

            {/* Balance Card */}
            <div style={styles.balanceCard}>
              <div style={styles.balanceLabel}>
                Available BAIT Cash
              </div>
              <div style={styles.balanceAmount}>
                {formatPkr(baitcashAccount?.balance || 0)}
              </div>
              {baitcashAccount?.phoneNumber && (
                <div style={styles.balanceUser}>
                  <UserIcon width={14} height={14} />
                  {baitcashAccount.fullName} •{" "}
                  {baitcashAccount.phoneNumber}
                </div>
              )}
            </div>

            {/* Recipient Phone */}
            <div style={styles.formContainer}>
              <label style={styles.formLabel}>
                <PhoneIcon
                  width={16}
                  height={16}
                  style={{ color: "#9a6a18" }}
                />{" "}
                Recipient Phone Number
              </label>
              <div style={styles.inputRow}>
                <input
                  type="tel"
                  ref={phoneInputRef}
                  value={zakatPhone}
                  onChange={(e) => {
                    setZakatPhone(e.target.value);
                    setRecipient(null);
                    setSendError("");
                  }}
                  onBlur={handlePhoneBlur}
                  onKeyDown={handlePhoneKeyDown}
                  placeholder="03XXXXXXXXX or +923XXXXXXXXX"
                  disabled={resolving || submitting}
                  style={styles.phoneInput}
                />
                <button
                  onClick={() => resolveRecipient()}
                  disabled={
                    resolving || !zakatPhone.trim() || submitting
                  }
                  style={styles.resolveButton}
                >
                  {resolving ? (
                    <Spin size="small" style={{ marginRight: 4 }} />
                  ) : (
                    "Find"
                  )}
                </button>
              </div>
              <div style={styles.formHelper}>
                Enter the phone number of the zakat recipient.
              </div>
              {recipient && (
                <div style={styles.recipientCard}>
                  <div style={styles.recipientAvatar}>
                    <UserIcon width={22} height={22} />
                  </div>
                  <div style={styles.recipientInfo}>
                    <p style={styles.recipientName}>
                      {recipient.fullName}
                    </p>
                    <p style={styles.recipientPhone}>
                      {recipient.phoneNumber}
                    </p>
                  </div>
                  <CheckCircleIcon
                    width={22}
                    height={22}
                    style={styles.recipientCheck}
                  />
                </div>
              )}
            </div>

            {/* Amount */}
            <div style={styles.formContainer}>
              <label style={styles.formLabel}>
                Zakat Amount (PKR)
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type="text"
                  inputMode="decimal"
                  value={zakatAmountPKR}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (
                      value === "" ||
                      /^\d*(\.\d{1,2})?$/.test(value)
                    ) {
                      setZakatAmountPKR(value);
                      setSendError("");
                    }
                  }}
                  placeholder="0.00"
                  disabled={submitting}
                  style={styles.amountInput}
                />
                <span
                  style={{
                    position: "absolute",
                    right: "16px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#8a7a62",
                  }}
                >
                  PKR
                </span>
              </div>
              {suggestedZakatBAIT > 0 && (
                <div style={styles.formHelper}>
                  Suggested Zakat amount (based on your eligibility):
                  ~{suggestedZakatBAIT.toFixed(2)} BAIT
                </div>
              )}
              <div style={styles.formHelper}>
                Enter the amount of Zakat you wish to send.
              </div>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSendZakat}
              disabled={submitting || !recipient || !zakatAmountPKR}
              style={styles.submitButton}
            >
              {submitting ? (
                <>
                  <Spin size="small" style={{ marginRight: 8 }} />
                  Sending...
                </>
              ) : (
                <>
                  Send Zakat
                  <ArrowRightIcon width={18} height={18} />
                </>
              )}
            </button>

            {/* Security Note */}
            <div style={styles.securityNote}>
              <LockClosedIcon
                width={14}
                height={14}
                style={styles.securityIcon}
              />
              <span>Secure • Transparent • 24/7</span>
              <span style={{ marginLeft: "auto", opacity: 0.7 }}>
                Your Zakat is sent with trust and security
              </span>
            </div>
          </div>
        ) : (
          // ---------- ELIGIBILITY FORM ----------
          <div style={styles.formContainer}>
            {eligibilityError && (
              <div style={styles.errorBox}>
                <InformationCircleIcon width={18} height={18} />
                <span>{eligibilityError}</span>
              </div>
            )}

            <div style={styles.eligibilityCard}>
              <form onSubmit={handleEligibilitySubmit}>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Full Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleEligibilityChange}
                      placeholder="Enter your full name"
                      style={styles.formInput}
                      required
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>CNIC</label>
                    <input
                      type="text"
                      name="cnic"
                      value={formData.cnic}
                      onChange={handleEligibilityChange}
                      placeholder="e.g. 35201-1234567-8"
                      style={styles.formInput}
                      required
                    />
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Age</label>
                    <select
                      name="age"
                      value={formData.age}
                      onChange={handleEligibilityChange}
                      style={styles.formSelect}
                      required
                    >
                      <option value="">-- Select Age --</option>
                      {Array.from(
                        { length: 83 },
                        (_, i) => i + 18
                      ).map((age) => (
                        <option key={age} value={age}>
                          {age}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>
                      Monthly Income
                    </label>
                    <select
                      name="income"
                      value={formData.income}
                      onChange={handleEligibilityChange}
                      style={styles.formSelect}
                      required
                    >
                      <option value="">-- Select --</option>
                      <option value="10000">0 - 10K</option>
                      <option value="20000">10K - 20K</option>
                      <option value="30000">20K - 30K</option>
                      <option value="40000">30K - 40K</option>
                    </select>
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>
                      Residential Status
                    </label>
                    <select
                      name="residentialStatus"
                      value={formData.residentialStatus}
                      onChange={handleEligibilityChange}
                      style={styles.formSelect}
                    >
                      <option value="">-- Select --</option>
                      <option value="Rented">Rented</option>
                      <option value="Owned">Owned</option>
                    </select>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>
                      Employment Status
                    </label>
                    <select
                      name="employment"
                      value={formData.employment}
                      onChange={handleEligibilityChange}
                      style={styles.formSelect}
                    >
                      <option value="">-- Select --</option>
                      <option value="Employed">Employed</option>
                      <option value="Unemployed">Unemployed</option>
                    </select>
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Support</label>
                    <select
                      name="support"
                      value={formData.support}
                      onChange={handleEligibilityChange}
                      style={styles.formSelect}
                    >
                      <option value="">-- Select --</option>
                      <option value="Self">Self Support</option>
                      <option value="Parents">Parents Support</option>
                    </select>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>
                      Liabilities (optional)
                    </label>
                    <input
                      type="text"
                      name="liabilities"
                      value={formData.liabilities}
                      onChange={handleEligibilityChange}
                      placeholder="e.g., 50000"
                      style={styles.formInput}
                    />
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>
                      Assets (optional)
                    </label>
                    <input
                      type="text"
                      name="assets"
                      value={formData.assets}
                      onChange={handleEligibilityChange}
                      placeholder="e.g., 200000"
                      style={styles.formInput}
                    />
                  </div>
                </div>

                <button type="submit" style={styles.submitButton}>
                  Check Eligibility
                </button>
              </form>
            </div>

            {eligibilityResult && (
              <div
                style={
                  eligible
                    ? styles.eligibilityResult
                    : styles.eligibilityError
                }
              >
                <strong>
                  {eligible ? "✅ Eligible" : "❌ Not Eligible"}
                </strong>
                <p style={{ marginTop: "4px", marginBottom: 0 }}>
                  {eligibilityResult}
                </p>
                {eligible && (
                  <p
                    style={{
                      marginTop: "8px",
                      fontSize: "12px",
                      color: "#065f46",
                    }}
                  >
                    You can now proceed to send Zakat. Click
                    "Continue" below.
                  </p>
                )}
                {eligible && (
                  <button
                    onClick={() => setStep("send")}
                    style={{
                      ...styles.submitButton,
                      marginTop: "12px",
                    }}
                  >
                    Continue to Send Zakat
                    <ArrowRightIcon width={18} height={18} />
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ZakatPage;